import { useCallback } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';

const FORM_SUBMIT_ADDR = 'https://readdy.ai/api/form/d9rfujnnahb7382ovlsg';

export default function AboutPage() {
  const handleSubmit = useCallback((e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const hpValue = (formData.get('website_alt') as string || '').trim();
    if (hpValue) {
      form.reset();
      const statusEl = document.getElementById('form-status');
      if (statusEl) {
        statusEl.textContent = '消息已发送，我们会尽快回复您！';
        statusEl.className = 'text-xs text-accent-600 mt-3 text-center';
      }
      return;
    }

    formData.delete('website_alt');

    const urlEncoded = new URLSearchParams();
    formData.forEach((value, key) => {
      urlEncoded.append(key, value as string);
    });

    fetch(FORM_SUBMIT_ADDR, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: urlEncoded.toString(),
    })
      .then(async (res) => {
        const responseText = await res.text();
        let serverMsg = '';
        try {
          const parsed = JSON.parse(responseText);
          serverMsg = parsed?.meta?.message || parsed?.message || '';
        } catch {
          // ignore
        }

        const statusEl = document.getElementById('form-status');
        if (!statusEl) return;

        if (res.ok && serverMsg !== 'form data is spam' && !serverMsg.includes('spam')) {
          form.reset();
          statusEl.textContent = '消息已发送，我们会尽快回复您！';
          statusEl.className = 'text-xs text-accent-600 mt-3 text-center';
        } else {
          statusEl.textContent = serverMsg || '发送失败，请稍后重试';
          statusEl.className = 'text-xs text-primary-600 mt-3 text-center';
        }
      })
      .catch(() => {
        const statusEl = document.getElementById('form-status');
        if (statusEl) {
          statusEl.textContent = '网络错误，请稍后重试';
          statusEl.className = 'text-xs text-primary-600 mt-3 text-center';
        }
      });
  }, []);

  const jsonLdOrg = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '开云爱体育',
    url: 'https://www.kaiyunsports.com',
    description: '开云爱体育是专业体育竞技娱乐平台，汇聚全球顶级赛事资源，提供多元化体育项目体验。',
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+86-400-888-9999',
      contactType: 'customer service',
      availableLanguage: ['Chinese'],
    },
    address: {
      '@type': 'PostalAddress',
      addressLocality: '北京',
      addressRegion: '朝阳区',
      streetAddress: '建国路88号',
    },
    sameAs: [
      'https://weibo.com/kaiyunsports',
      'https://weixin.qq.com/kaiyunsports',
    ],
  };

  const jsonLdFAQ = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '开云爱体育是什么平台？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '开云爱体育是一个专业的体育竞技娱乐平台，汇聚全球顶级赛事资源，提供足球、篮球、网球、电竞、赛车、搏击等多元体育项目体验。平台致力于为体育爱好者提供安全、便捷、专业的体育娱乐服务。',
        },
      },
      {
        '@type': 'Question',
        name: '如何注册开云爱体育账号？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以通过开云爱体育官方网站联系客服完成注册。注册过程简单快捷，只需提供基本信息即可开启您的体育之旅。',
        },
      },
      {
        '@type': 'Question',
        name: '开云爱体育平台是否安全可靠？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '开云爱体育始终将用户安全放在首位。平台采用先进的加密技术和严格的数据保护措施，确保用户信息和资金安全。我们拥有正规运营资质，信誉良好，值得信赖。',
        },
      },
      {
        '@type': 'Question',
        name: '开云爱体育支持哪些体育项目？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '开云爱体育涵盖足球、篮球、网球、电竞、赛车、搏击等数十种热门体育项目。我们实时追踪全球顶级赛事，提供全面的赛事资讯和专业分析。',
        },
      },
    ],
  };

  return (
    <>
      <Navbar />
      <main>
        <div className="pt-16 bg-background-50">
          <script type="application/ld+json">{JSON.stringify(jsonLdOrg)}</script>
          <script type="application/ld+json">{JSON.stringify(jsonLdFAQ)}</script>

          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />

          {/* Brand Introduction */}
          <section className="w-full px-4 md:px-6 py-14 md:py-20">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-14">
                <span className="inline-block px-3 py-1 text-xs font-medium text-primary-700 bg-primary-100 rounded-full mb-4">
                  品牌故事
                </span>
                <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4">
                  关于开云爱体育
                </h1>
                <p className="text-sm text-foreground-500 max-w-3xl mx-auto leading-relaxed">
                  开云爱体育成立于2018年，是一家专注于体育竞技娱乐的综合性平台。我们汇聚全球顶级赛事资源，致力于为体育爱好者提供最专业、最全面、最安全的体育娱乐服务。
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center mb-16">
                <div className="relative h-72 md:h-96 rounded-2xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20luxury%20sports%20lounge%20with%20large%20screens%20showing%20live%20sports%20events%20elegant%20interior%20warm%20golden%20lighting%20professional%20atmosphere%20high%20end%20sports%20bar%20design&width=800&height=600&seq=about-brand-story&orientation=landscape"
                    alt="开云爱体育品牌"
                    title="开云爱体育品牌介绍"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-5">
                    汇聚全球赛事，服务中国体育爱好者
                  </h2>
                  <div className="space-y-4 text-sm text-foreground-600 leading-relaxed">
                    <p>
                      开云爱体育自创立以来，始终秉持"专业、安全、诚信、创新"的核心价值观，致力于打造国内领先的体育竞技娱乐平台。我们与全球各大体育赛事组织保持紧密合作，为用户提供最及时的赛事资讯和最专业的分析解读。
                    </p>
                    <p>
                      平台汇聚了英超、西甲、NBA、温网、F1、UFC等数百项全球顶级赛事资源，涵盖足球、篮球、网球、电竞、赛车、搏击等数十种热门体育项目。无论您是哪种体育运动的爱好者，都能在开云爱体育找到属于自己的精彩。
                    </p>
                    <p>
                      我们拥有一支由资深体育评论员、数据分析和专业编辑组成的团队，每天24小时不间断地为用户提供最新、最准确、最深入的体育资讯和赛事分析。
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Platform Advantages */}
          <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-12">
                <span className="inline-block px-3 py-1 text-xs font-medium text-accent-700 bg-accent-100 rounded-full mb-4">
                  平台优势
                </span>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                  为什么选择开云爱体育
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                  {
                    icon: 'ri-trophy-line',
                    title: '赛事资源丰富',
                    desc: '汇聚全球200+顶级赛事，覆盖数十种体育项目，满足不同爱好者的需求。',
                  },
                  {
                    icon: 'ri-shield-check-line',
                    title: '安全可靠保障',
                    desc: '采用银行级加密技术和严格数据保护措施，全方位保障用户信息和资金安全。',
                  },
                  {
                    icon: 'ri-customer-service-2-line',
                    title: '专业贴心服务',
                    desc: '7x24小时专业客服团队随时待命，快速响应用户需求，解决您的每一个问题。',
                  },
                  {
                    icon: 'ri-flashlight-line',
                    title: '极速流畅体验',
                    desc: '平台采用先进技术架构，确保赛事资讯实时更新，页面加载速度飞快。',
                  },
                ].map((adv, i) => (
                  <div key={i} className="bg-background-50 rounded-2xl p-6 border border-background-200/70 text-center">
                    <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4 rounded-xl bg-accent-100">
                      <i className={`${adv.icon} text-xl text-accent-600`}></i>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-2">{adv.title}</h3>
                    <p className="text-xs text-foreground-500 leading-relaxed">{adv.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Reputation / Stats */}
          <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-12">
                <span className="inline-block px-3 py-1 text-xs font-medium text-primary-700 bg-primary-100 rounded-full mb-4">
                  信誉展示
                </span>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                  数据见证实力
                </h2>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {[
                  { number: '500,000+', label: '注册用户' },
                  { number: '200+', label: '覆盖赛事' },
                  { number: '99.9%', label: '平台稳定性' },
                  { number: '98%', label: '用户好评率' },
                ].map((stat, i) => (
                  <div key={i} className="bg-background-100 rounded-2xl p-6 text-center border border-background-200/70">
                    <p className="text-2xl md:text-3xl font-bold text-accent-600 mb-1">{stat.number}</p>
                    <p className="text-sm text-foreground-500">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Contact Section */}
          <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-12">
                <span className="inline-block px-3 py-1 text-xs font-medium text-accent-700 bg-accent-100 rounded-full mb-4">
                  联系我们
                </span>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                  期待与您取得联系
                </h2>
                <p className="mt-3 text-sm text-foreground-500 max-w-xl mx-auto">
                  无论您有任何疑问或建议，我们的客服团队都将竭诚为您服务
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                <div className="space-y-5">
                  {[
                    { icon: 'ri-mail-line', title: '电子邮箱', value: 'contact@kaiyunsports.com' },
                    { icon: 'ri-phone-line', title: '客服电话', value: '400-888-9999' },
                    { icon: 'ri-map-pin-line', title: '公司地址', value: '北京市朝阳区建国路88号SOHO现代城A座' },
                    { icon: 'ri-time-line', title: '服务时间', value: '周一至周日 9:00 - 22:00' },
                  ].map((contact, i) => (
                    <div key={i} className="flex items-start gap-4 bg-background-50 rounded-2xl p-5 border border-background-200/70">
                      <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-accent-100 flex-shrink-0">
                        <i className={`${contact.icon} text-lg text-accent-600`}></i>
                      </div>
                      <div>
                        <h4 className="text-xs text-foreground-500 mb-1">{contact.title}</h4>
                        <p className="text-sm font-medium text-foreground-900">{contact.value}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="bg-background-50 rounded-2xl p-6 border border-background-200/70">
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-4">发送消息</h3>
                  <form className="space-y-4" data-readdy-form="contact" onSubmit={handleSubmit}>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-foreground-600 mb-1.5">姓名</label>
                        <input
                          type="text"
                          name="name"
                          required
                          className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-300 rounded-lg focus:outline-none focus:border-primary-400 transition-colors"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-foreground-600 mb-1.5">邮箱</label>
                        <input
                          type="email"
                          name="email"
                          required
                          className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-300 rounded-lg focus:outline-none focus:border-primary-400 transition-colors"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-foreground-600 mb-1.5">主题</label>
                      <input
                        type="text"
                        name="subject"
                        required
                        className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-300 rounded-lg focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入消息主题"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-foreground-600 mb-1.5">消息内容</label>
                      <textarea
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-300 rounded-lg focus:outline-none focus:border-primary-400 transition-colors resize-none"
                        placeholder="请输入您的消息（最多500字）"
                      ></textarea>
                    </div>
                    <input
                      type="text"
                      name="website_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      readOnly
                      className="hp-field"
                    />
                    <button
                      type="submit"
                      className="w-full py-3 bg-primary-500 text-white text-sm font-semibold rounded-full hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      发送消息
                    </button>
                    <div id="form-status"></div>
                  </form>
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}